/* eslint-disable no-unused-vars */
import { useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ModalBase, ModalHeader, ModalFooter, ChooseBase } from 'components';
import * as S from './ChooseLocationModal.styled';
import { useSignal } from '@preact/signals-react';
import { ChooseContent } from './chooseContent/ChooseContent';
import { SearchChooseContent } from './searchChooseContent/SearchChooseContent';
import { SelectedContent } from './selectedContent/SelectedContent';
import {
  useSet,
  useDebounce,
  createLocationsStrToSave,
  goToLocationParent,
  getMinTierValue,
} from 'utils';
import { searchLocationsCitysAPI } from 'services/searchLocationsCitysAPI.service';
import { selectedLocationsList, select_tier_locations } from 'store/selectors';
import {
  generalActions,
  targetingPlacementsActions,
  formErrorsCampaign_action,
} from 'store/actions';

const toggleSet = (element) => (set) => {
  if (set.has(element)) {
    return set.delete(element);
  }
  set.add(element);
};

function createUniqueIdToItem(data, fieldName) {
  if (!data) return null;
  const resultMap = new Map();
  data?.forEach((item) => {
    resultMap.set(item[fieldName], item);
    if (item.countries) {
      item.countries.forEach((country) => {
        resultMap.set(country[fieldName], country);
        if (country.regions) {
          country.regions.forEach((region) => {
            resultMap.set(region[fieldName], region);
          });
        }
      });
    }
  });
  return resultMap;
}

export const ChooseLocationModal = ({ close, title }) => {
  const searchValue$ = useSignal('');
  const changesFlag = useRef(false);
  const dispatch = useDispatch();
  const selectedItemsAfterSearch$ = useSignal(new Map());
  const selected_locations = useSelector(selectedLocationsList);

  const tireCountriesMap = useSelector(select_tier_locations);

  const { data, isSuccess, isError, isLoading } =
    searchLocationsCitysAPI.useGetInitLocationsListQuery();

  const [searchAction, searchResult] =
    searchLocationsCitysAPI.useLazyFetchSearchLocationsCitysQuery();
  const debouncedSearchValue = useDebounce(searchValue$.value, 400);

  useEffect(() => {
    if (debouncedSearchValue.length >= 3) {
      searchAction(debouncedSearchValue);
    }
  }, [debouncedSearchValue]);

  const searchIdToItemMap = useMemo(() => {
    return createUniqueIdToItem(searchResult.data, 'id');
  }, [searchResult.data]);

  const uniqueIdToItemMap = useMemo(() => {
    return createUniqueIdToItem(data, 'uniq_id');
  }, [data]);

  const uniqueIdToItemMapFromServer = useMemo(() => {
    return createUniqueIdToItem(selected_locations, 'id');
  }, [selected_locations]);

  const include_items = selected_locations
    .filter(({ status }) => status === 'include')
    .map(({ value }) => value);

  const exclude_items = selected_locations
    .filter(({ status }) => status === 'exclude')
    .map(({ value }) => value);

  const [includeSet, setIncludeSet] = useSet(include_items);
  const [excludeSet, setExcludeSet] = useSet(exclude_items);

  const isActiveItem = (countryOrRegionItem, fieldName) => {
    const continentId = goToLocationParent(countryOrRegionItem[fieldName], 'continent');
    const countryId = goToLocationParent(countryOrRegionItem[fieldName], 'country');
    const regionId = goToLocationParent(countryOrRegionItem[fieldName], 'region');

    if (excludeSet.has(countryOrRegionItem[fieldName])) return false;
    if (includeSet.has(countryOrRegionItem[fieldName])) return true;
    if (countryOrRegionItem.type === 'country') {
      if (includeSet.has(continentId)) return true;
    }
    if (countryOrRegionItem.type === 'region') {
      if (includeSet.has(countryId)) return true;
      if (excludeSet.has(countryId)) return false;
      if (includeSet.has(continentId)) return true;
    }
    if (countryOrRegionItem.type === 'city') {
      if (includeSet.has(continentId)) return true;
      if (includeSet.has(countryId)) return true;
      if (includeSet.has(regionId)) return true;
      return includeSet.has(countryOrRegionItem[fieldName]);
    }
  };

  const childrenIsExcluded = (countryOrRegionItem, fieldName) => {
    if (countryOrRegionItem.type === 'country') {
      const transformIncludedSet = new Set(
        [...includeSet].map((uniq_id) => goToLocationParent(uniq_id, 'country')),
      );
      return transformIncludedSet.has(countryOrRegionItem[fieldName]);
    }
    if (countryOrRegionItem.type === 'region') {
      const transformIncludedSet = new Set(
        [...includeSet].map((uniq_id) => goToLocationParent(uniq_id, 'region')),
      );
      return transformIncludedSet.has(countryOrRegionItem[fieldName]);
    }
  };

  const toggleCheckedCountryOrRegion = (countryOrRegionItem, fieldName) => {
    if (!changesFlag.current) changesFlag.current = true;
    if (countryOrRegionItem.type === 'country') {
      const continentId = goToLocationParent(countryOrRegionItem[fieldName], 'continent');
      if (includeSet.has(continentId)) {
        setExcludeSet((newExcludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newExcludeSet);
        });
        setExcludeSet((newExcludeSet) =>
          [...excludeSet].forEach((uniq_id) => {
            const goToCountry = goToLocationParent(uniq_id, 'country');
            if (goToCountry === countryOrRegionItem[fieldName])
              return newExcludeSet.delete(uniq_id);
          }),
        );
      }
      if (!includeSet.has(continentId)) {
        setExcludeSet((newExcludeSet) =>
          [...excludeSet].forEach((uniq_id) => {
            const goToCountry = goToLocationParent(uniq_id, 'country');
            if (goToCountry === countryOrRegionItem[fieldName])
              return newExcludeSet.delete(uniq_id);
          }),
        );
        setIncludeSet((newIncludeSet) =>
          [...includeSet].forEach((uniq_id) => {
            const goToCountry = goToLocationParent(uniq_id, 'country');
            if (
              uniq_id !== countryOrRegionItem[fieldName] &&
              goToCountry === countryOrRegionItem[fieldName]
            )
              return newIncludeSet.delete(uniq_id);
          }),
        );
        setIncludeSet((newIncludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
        });
      }
    }
    if (countryOrRegionItem.type === 'region') {
      const countryId = goToLocationParent(countryOrRegionItem[fieldName], 'country');
      const continentId = goToLocationParent(countryOrRegionItem[fieldName], 'continent');

      if (includeSet.has(continentId) && !excludeSet.has(countryId)) {
        setExcludeSet((newExcludeSet) =>
          [...excludeSet].forEach((uniq_id) => {
            const goToRegion = goToLocationParent(uniq_id, 'region');
            if (
              uniq_id !== countryOrRegionItem[fieldName] &&
              goToRegion === countryOrRegionItem[fieldName]
            )
              return newExcludeSet.delete(uniq_id);
          }),
        );
        setExcludeSet((newIncludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
        });
      }
      if (includeSet.has(continentId) && excludeSet.has(countryId)) {
        setIncludeSet((newIncludeSet) =>
          [...includeSet].forEach((uniq_id) => {
            const goToRegion = goToLocationParent(uniq_id, 'region');
            if (
              uniq_id !== countryOrRegionItem[fieldName] &&
              goToRegion === countryOrRegionItem[fieldName]
            )
              return newIncludeSet.delete(uniq_id);
          }),
        );
        setIncludeSet((newIncludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
        });
      }
      if (!includeSet.has(continentId) && includeSet.has(countryId)) {
        setExcludeSet((newExcludeSet) =>
          [...excludeSet].forEach((uniq_id) => {
            const goToRegion = goToLocationParent(uniq_id, 'region');
            if (
              uniq_id !== countryOrRegionItem[fieldName] &&
              goToRegion === countryOrRegionItem[fieldName]
            )
              return newExcludeSet.delete(uniq_id);
          }),
        );
        setExcludeSet((newIncludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
        });
      }
      if (!includeSet.has(continentId) && !includeSet.has(countryId)) {
        setIncludeSet((newIncludeSet) =>
          [...includeSet].forEach((uniq_id) => {
            const goToRegion = goToLocationParent(uniq_id, 'region');
            if (
              uniq_id !== countryOrRegionItem[fieldName] &&
              goToRegion === countryOrRegionItem[fieldName]
            )
              return newIncludeSet.delete(uniq_id);
          }),
        );
        setIncludeSet((newIncludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
        });
      }
    }
    if (countryOrRegionItem.type === 'continent') {
      setIncludeSet((newIncludeSet) => {
        toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
      });
      setIncludeSet((newIncludeSet) =>
        [...includeSet].forEach((uniq_id) => {
          const goToContinent = goToLocationParent(uniq_id, 'continent');
          if (
            uniq_id !== countryOrRegionItem[fieldName] &&
            goToContinent === countryOrRegionItem[fieldName]
          )
            return newIncludeSet.delete(uniq_id);
        }),
      );
    }
    if (countryOrRegionItem.type === 'city') {
      const goToContinent = goToLocationParent(countryOrRegionItem[fieldName], 'continent');
      const goToCountry = goToLocationParent(countryOrRegionItem[fieldName], 'country');
      const goToRegion = goToLocationParent(countryOrRegionItem[fieldName], 'region');
      if (
        includeSet.has(goToContinent) &&
        !excludeSet.has(goToCountry) &&
        !excludeSet.has(goToRegion)
      ) {
        setExcludeSet((newExcludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newExcludeSet);
        });
      }
      if (
        includeSet.has(goToContinent) &&
        (excludeSet.has(goToCountry) || excludeSet.has(goToRegion))
      ) {
        setIncludeSet((newIncludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
        });
      }
      if (
        !includeSet.has(goToContinent) &&
        !includeSet.has(goToCountry) &&
        !includeSet.has(goToRegion)
      ) {
        setIncludeSet((newIncludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newIncludeSet);
        });
      }
      if (
        includeSet.has(goToContinent) ||
        includeSet.has(goToCountry) ||
        includeSet.has(goToRegion)
      ) {
        setExcludeSet((newExcludeSet) => {
          toggleSet(countryOrRegionItem[fieldName])(newExcludeSet);
        });
      }
    }
  };

  const getItemData = useCallback(
    (id) => {
      const itemData =
        uniqueIdToItemMap.get(id) ??
        uniqueIdToItemMapFromServer.get(id) ??
        searchIdToItemMap.get(id) ??
        selectedItemsAfterSearch$.value.get(id);
      return itemData;
    },
    [
      uniqueIdToItemMap,
      uniqueIdToItemMapFromServer,
      searchIdToItemMap,
      selectedItemsAfterSearch$.value,
    ],
  );

  const saveButtonAction = () => {
    const includeObjArray = [...includeSet].map((incId) => ({
      ...getItemData(incId),
      status: 'include',
      value: incId,
    }));

    const minTierValue = getMinTierValue(includeObjArray, tireCountriesMap);

    const excludeObjArray = [...excludeSet].map((excId) => ({
      ...getItemData(excId),
      status: 'exclude',
      value: excId,
    }));
    // const dataForSavingInDataBase = `${[...includeSet].join(',')}|${[...excludeSet].join(',')}`;
    dispatch(generalActions.setTierField(minTierValue));
    dispatch(generalActions.updateSelectedLocations([...includeObjArray, ...excludeObjArray]));
    dispatch(
      generalActions.updateSelectedLocationsForSaving(
        createLocationsStrToSave([...includeSet], [...excludeSet]),
      ),
    );
    if (includeObjArray.length) {
      dispatch(
        formErrorsCampaign_action.updateErrorField({
          errorField: 'targeting',
          newValue: false,
        }),
      );
      dispatch(
        targetingPlacementsActions.updateErrorField({
          errorField: 'locations',
          newValue: false,
        }),
      );
    }
    close();
  };

  const saveBtnIsDisabled = useMemo(() => {
    return Boolean(includeSet.size) && changesFlag.current;
  }, [includeSet.size, changesFlag.current]);

  return (
    <ModalBase>
      <S.Box>
        <ModalHeader
          title={title}
          close={close}
          message='Select Region / Country to Include. Deselect Country, City to Exclude.'
        />
        <div style={{ display: 'flex', width: 1000 }}>
          <div style={{ width: 400 }}>
            <ChooseBase.Divided
              title='Search Country, City'
              searchValue={searchValue$.value}
              searchOnChange={(e) => {
                searchValue$.value = e.target.value;
              }}
              handleClearSearchValue={() => {
                searchValue$.value = '';
              }}
            >
              {debouncedSearchValue.length < 3 ? (
                <ChooseContent
                  childrenIsExcluded={childrenIsExcluded}
                  includeSet={includeSet}
                  setIncludeSet={setIncludeSet}
                  excludeSet={excludeSet}
                  setExcludeSet={setExcludeSet}
                  isActiveItem={isActiveItem}
                  toggleCheckedCountryOrRegion={toggleCheckedCountryOrRegion}
                />
              ) : (
                <SearchChooseContent
                  includeSet={includeSet}
                  excludeSet={excludeSet}
                  searchResult={searchResult}
                  isActiveItem={isActiveItem}
                  childrenIsExcluded={childrenIsExcluded}
                  toggleCheckedCountryOrRegion={toggleCheckedCountryOrRegion}
                  selectedItemsAfterSearch$={selectedItemsAfterSearch$}
                />
              )}
            </ChooseBase.Divided>
          </div>
          <SelectedContent
            uniqueIdToItemMap={uniqueIdToItemMap}
            excludeSet={excludeSet}
            includeSet={includeSet}
            setExcludeSet={setExcludeSet}
            setIncludeSet={setIncludeSet}
            uniqueIdToItemMapFromServer={uniqueIdToItemMapFromServer}
            searchIdToItemMap={searchIdToItemMap}
            selectedItemsAfterSearch$={selectedItemsAfterSearch$}
            changesFlag={changesFlag}
          />
        </div>

        <ModalFooter
          saveButtonText='Apply'
          saveButtonOnClick={saveButtonAction}
          close={close}
          saveButtonDisabled={!saveBtnIsDisabled}
        />
      </S.Box>
    </ModalBase>
  );
};
